import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ProBadge } from "@/components/ui/ProBadge";
import { PanelBottom, Shield, Sparkles } from "lucide-react";

interface FooterSubViewProps {
  currentDesign: any;
  updateTransientPreview: (updates: any) => void;
  isPro: boolean;
  onUpgradeClick: (featureKey?: string) => void;
}

export function FooterSubView({
  currentDesign,
  updateTransientPreview,
  isPro,
  onUpgradeClick,
}: FooterSubViewProps) {
  const currentLayout = currentDesign.layoutOverrides || {};
  const showWatermark = currentLayout.showWatermark !== false;
  const customFooterText = currentLayout.customFooterText || "";

  const currentYear = new Date().getFullYear();
  const authorName = currentDesign.fullName || "Your Name";

  const handleUpdateWatermark = (checked: boolean) => {
    if (!isPro && !checked) {
      onUpgradeClick("remove_branding");
      return;
    }
    updateTransientPreview({
      layoutOverrides: {
        ...currentLayout,
        showWatermark: checked,
      },
    });
  };

  const handleUpdateCustomText = (text: string) => {
    updateTransientPreview({
      layoutOverrides: {
        ...currentLayout,
        customFooterText: text,
      },
    });
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="border-b border-border pb-3">
        <h3 className="text-base font-bold text-foreground">Footer & Branding</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Customize your published site's footer notice, copyright statement, and Profolio branding watermark.
        </p>
      </div>

      {/* ── 1. Branding & Watermark ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Platform Branding
          </Label>
        </div>

        <div className="flex items-center justify-between p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-xs">
          <div className="space-y-1 max-w-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">"Powered by Profolio" Watermark</span>
              {!isPro && <ProBadge locked />}
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              Display a subtle, elegant watermark badge in your published portfolio's bottom bar. Pro members can remove or customize this branding.
            </div>
          </div>
          <Switch
            checked={showWatermark}
            onCheckedChange={handleUpdateWatermark}
          />
        </div>
      </div>

      {/* ── 2. Custom Footer Notice & Copyright ── */}
      <div className="space-y-3 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PanelBottom className="h-4 w-4 text-primary" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Custom Footer Statement / Copyright
            </Label>
          </div>
          <span className="text-[11px] font-mono text-muted-foreground">
            {customFooterText.length}/200 chars
          </span>
        </div>

        <div className="p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-xs space-y-4">
          <div className="space-y-1.5">
            <input
              type="text"
              maxLength={200}
              value={customFooterText}
              onChange={(e) => handleUpdateCustomText(e.target.value)}
              placeholder={`e.g. © ${currentYear} ${authorName}. All rights reserved.`}
              className="w-full h-10 px-3.5 text-sm rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-primary transition-all"
            />
          </div>

          {/* Quick Preset Buttons */}
          <div className="space-y-2">
            <span className="text-[11px] font-medium text-muted-foreground">1-Click Presets:</span>
            <div className="flex flex-wrap gap-2">
              {[
                {
                  label: "Standard Copyright",
                  text: `© ${currentYear} ${authorName}. All rights reserved.`,
                },
                {
                  label: "Academic & Research",
                  text: `© ${currentYear} ${authorName} · Academic Research Portfolio.`,
                },
                {
                  label: "Dissemination Notice",
                  text: `Built for scholarly dissemination and scientific collaboration.`,
                },
              ].map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handleUpdateCustomText(preset.text)}
                  className="px-2.5 py-1 rounded-lg border border-border/80 bg-muted/40 hover:bg-muted text-[11px] text-muted-foreground hover:text-foreground font-medium transition-colors cursor-pointer"
                >
                  {preset.label}
                </button>
              ))}
              {customFooterText && (
                <button
                  type="button"
                  onClick={() => handleUpdateCustomText("")}
                  className="px-2.5 py-1 rounded-lg border border-border/80 bg-transparent hover:bg-destructive/10 hover:text-destructive text-[11px] text-muted-foreground font-medium transition-colors cursor-pointer"
                >
                  Clear Text
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. Live Footer Mockup Preview ── */}
      <div className="space-y-3 border-t border-border pt-6">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Simulated Footer Appearance
          </Label>
        </div>

        <div className="p-6 rounded-2xl border border-border/80 bg-muted/30 flex flex-col items-center justify-center gap-3 text-center select-none">
          <div className="text-xs text-muted-foreground max-w-md">
            {customFooterText || `© ${currentYear} ${authorName}. All rights reserved.`}
          </div>

          {showWatermark && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border/60 shadow-2xs text-[11px] font-medium text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span>Powered by <strong>Profolio</strong></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
